import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Grid,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddIcon from '@mui/icons-material/Add';
import notaCreditoVentaService, { NotaCreditoVenta } from '../../services/notaCreditoVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { getApiErrorMessage } from '../../utils/apiError';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';

const NotasCredito: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [notasCredito, setNotasCredito] = useState<NotaCreditoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedNotaCredito, setSelectedNotaCredito] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);

  const fetchNotasCredito = async (pageNumber: number, pageSize: number, search: string, desde: string, hasta: string) => {
    try {
      setLoading(true);
      // Asegurar que pageSize esté entre 1 y 20 (límite del backend)
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await notaCreditoVentaService.getNotasCreditoVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined
      });

      setNotasCredito(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar notas de crédito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las notas de crédito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      setNotasCredito([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchNotasCredito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const response = await api.get(
        `/api/NotasCreditoVenta/${nroFormulario}/pdf`,
        {
          responseType: 'blob',
        }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedNotaCredito(nroFormulario);
    } catch (error: any) {
      console.error('Error al generar el PDF de la nota de crédito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al generar el PDF de la nota de crédito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedNotaCredito(null);
    setPdfFileName(null);
  };

  const handleObtenerCae = async (nroFormulario: number) => {
    try {
      setObteniendoCae(nroFormulario);
      const notaCreditoActualizada = await notaCreditoVentaService.obtenerCaeArgentina(nroFormulario);
      
      setNotasCredito(prevNotasCredito => 
        prevNotasCredito.map(n => 
          n.nroFormulario === nroFormulario 
            ? { ...n, caeNro: notaCreditoActualizada.caeNro, caeVencimiento: notaCreditoActualizada.caeVencimiento }
            : n
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (notaCredito: NotaCreditoVenta) => {
    const { letra, nroSuc, nroComprobante } = notaCredito;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  // Función para obtener el chip de estado según el CAE
  const getEstadoChip = (caeNro?: string) => {
    if (caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }
    return {
      label: 'Sin CAE',
      bgColor: '#ff9800',
      textColor: '#ffffff'
    };
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Notas de Crédito de Venta
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/ventas/notas-credito/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nueva Nota de Crédito
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {notasCredito.map((notaCredito) => {
                const estadoChip = getEstadoChip(notaCredito.caeNro);
                const comprobante = formatComprobante(notaCredito);
                return (
                  <Card 
                    key={notaCredito.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Nota de Crédito #{notaCredito.nroFormulario}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {notaCredito.nombre?.trim() || '-'}
                            </Typography>
                          </Box>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(notaCredito.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {notaCredito.nroCliente || '-'}
                        </Typography>
                        {notaCredito.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {notaCredito.cuit.trim()}
                          </Typography>
                        )}
                        {comprobante !== '-' && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Comprobante:</strong> {comprobante}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(notaCredito.importeTotal)}
                        </Typography>
                        {notaCredito.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {notaCredito.usuario}
                          </Typography>
                        )}
                        {notaCredito.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {notaCredito.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          <VerAsientoContableButton iconOnly nroAsiento={notaCredito.nroAsiento} />
                          {!notaCredito.caeNro && (
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(notaCredito.nroFormulario)}
                                size="small"
                                disabled={obteniendoCae === notaCredito.nroFormulario}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1976d2' },
                                  '&:disabled': { bgcolor: '#ccc' }
                                }}
                              >
                                {obteniendoCae === notaCredito.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(notaCredito.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === notaCredito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' },
                                '&:disabled': { bgcolor: '#ccc' }
                              }}
                            >
                              {pdfLoading === notaCredito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasCredito.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay notas de crédito para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table 
            size={isTablet ? 'small' : 'medium'} 
            sx={{ 
              minWidth: 900,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem', lg: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100 }}>Nro. NC</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 90 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 70 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 150, maxWidth: 200 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>CUIT</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 100 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notasCredito.map((notaCredito) => {
                const estadoChip = getEstadoChip(notaCredito.caeNro);
                return (
                  <TableRow 
                    key={notaCredito.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{notaCredito.nroFormulario}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(notaCredito.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {formatComprobante(notaCredito)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{notaCredito.nroCliente || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {notaCredito.nombre?.trim() || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{notaCredito.cuit?.trim() || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">{formatCurrency(notaCredito.importeTotal)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{notaCredito.usuario || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                      <Chip
                        label={estadoChip.label}
                        size="small"
                        sx={{
                          bgcolor: estadoChip.bgColor,
                          color: estadoChip.textColor,
                          fontWeight: 500,
                          borderRadius: 1.5,
                          minWidth: 90,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <VerAsientoContableButton iconOnly nroAsiento={notaCredito.nroAsiento} />
                        {!notaCredito.caeNro && (
                          <Tooltip title="Obtener CAE de Argentina">
                            <IconButton
                              onClick={() => handleObtenerCae(notaCredito.nroFormulario)}
                              size="small"
                              disabled={obteniendoCae === notaCredito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s'
                              }}
                            >
                              {obteniendoCae === notaCredito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(notaCredito.nroFormulario)}
                            size="small"
                            disabled={pdfLoading === notaCredito.nroFormulario}
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { 
                                bgcolor: '#1976d2',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                              '&:disabled': { bgcolor: '#ccc' }
                            }}
                          >
                            {pdfLoading === notaCredito.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && notasCredito.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasCredito.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay notas de crédito para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedNotaCredito && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Nota de Crédito ${selectedNotaCredito}`}
          documentId={`${selectedNotaCredito}`}
          documentType="nota-credito"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
    </Box>
  );
};

export default NotasCredito;
